import React from 'react';
import classNames from 'classnames';

import Data from 'data';
import Locale from 'data/locale';
import Store from 'store';

import NumberInput from 'ui/number';
import { baseNoDurability, findClosestMod, findLowestMod, findModGroup, formatRanges, formatStr, gemStats, itemCheckMod, itemCountMods, itemDefenseRange, itemGetColor,
  itemGetModGroups, itemGetName, itemGetTypes, itemMaxSockets, itemMinLevel, modsToStats, randomRareName, runewordStats, statsToLines, uniqueStats } from 'logic/item';
import Tooltip from 'ui/tooltip';
import { Color, Quality } from 'common';
import { formatNumber, replaceReact } from 'utils/format';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import Select, { underlinerFunc } from 'ui/select';

import './ItemEditor.scss';
import { D2ItemTooltip } from 'tooltips';
import { presetItems, craftingRecipes, isSubType } from 'logic/itemTypes';
import { itemCreateNormal, itemCreateRuneword, itemCreateUnique, itemSetBase, itemSetQuality, itemUpdate } from 'logic/itemReducers';
import EditingContext from 'store/editingContext';
import ItemImage from 'components/ItemImage';
import { useDragEx, useItemMover } from 'components/dragDrop';
import itemProbability from 'logic/itemProbability';
import GameContext from 'gamectx';
import { defaultState, serializeState } from 'tools/common';
import AdUnit from 'AdUnit';

const _L = Locale("ui-equipment");

function HoverNumber({ disabled, value=0, ...props }) {
  const [editing, setEditing] = React.useState(false);
  const [hover, setHover] = React.useState(false);

  const onMouseOver = React.useCallback(() => setHover(true), []);
  const onMouseOut = React.useCallback(() => setHover(false), []);

  const onFocus = React.useCallback(() => setEditing(true), []);
  const onBlur = React.useCallback(() => setEditing(false), []);

  const max = Math.max(Math.abs(props.min), Math.abs(props.max));
  let digits = Math.floor(Math.log10(max)) + 1;
  if (props.digits !== 0) {
    digits += 0.6 + (props.digits || 2);
  }
  const width = 13 * digits + 28;

  const content = (
    <span className={classNames("d2p-HoverNumber", {"d2p-disabled": disabled})} onMouseEnter={onMouseOver} onMouseLeave={onMouseOut}>
      {value}
      {!!(!disabled && (hover || editing)) && <NumberInput value={value} onFocus={onFocus} onBlur={onBlur} style={{width}} {...props}/>}
    </span>
  );

  if (props.min != null && props.max != null && !props.values && !disabled) {
    return (
      <Tooltip tooltip={`${props.min}\u2013${props.max}`} align="left">
        {content}
      </Tooltip>
    );
  }

  return content;
}

const ItemModTip = ({ title, id: curId, group, ranges }) => (
  <div className="d2p-ItemModTip">
    <h3>{title}</h3>
    {group.map(id => {
      const mod = Data.mods[id];
      if (!mod) return null;
      const values = [];
      const modmin = new Map(), modmax = new Map();
      for (let i = 1; i <= 4; ++i) {
        const min = mod[`mod${i}min`], max = mod[`mod${i}max`];
        if (min != null && max != null) {
          const rng = ranges[i - 1];
          if (!rng || rng[0] === rng[rng.length - 1]) {
            values[i - 1] = max;
          } else {
            modmin.set(i, min);
            modmax.set(i, max);
            values[i - 1] = `{${i}}`;
          }
        }
      }
      const stats = modsToStats({}, {[id]: values});
      const lines = statsToLines(stats);
      const text = lines.map(line => {
        const m = line[1].match(/{.*}/);
        if (!m) return null;
        const rng = formatRanges(m[0], modmin, modmax);
        const m2 = rng.match(/^(\+?)\(([^)]*)\)$/);
        if (m2) return m2[1] + m2[2];
        return rng;
      }).filter(Boolean).join(" / ");
      const suffix = Locale.strings[mod.name] || mod.name;
      const ilvlTip = (mod.level ? _L`iLvl ${mod.level}` + ': ' : '');
      return <div key={id} className={classNames("d2p-ModTier", {"d2p-current": id === curId})}>{ilvlTip}{text}{suffix ? ` (${suffix})` : ''}</div>;
    })}
  </div>
);

function ItemModInfo({ id, group, ranges }) {
  let index = group.length - group.indexOf(id);
  let type = "T", title = null;
  if (Data.magicPrefix[id]) {
    type = "P";
    title = _L`Prefix`;
  } else if (Data.magicSuffix[id]) {
    type = "S";
    title = _L`Suffix`;
  } else if (Data.autoMagic[id]) {
    type = "A";
    title = _L`Auto Mod`;
  } else if (Data.crafted[id]) {
    type = "C";
    index = "";
    title = Data.crafted[id].description;
    title = craftingRecipes[title] || title || 'Crafted';
    title = _L(title);
  } else if (Data.qualityItems[id]) {
    type = "Q";
    index = "";
    title = _L`Superior Mod`;
  } else if (Data.staffMods[id]) {
    type = "M";
    index = "";
    title = _L`Staff Mod`;
  } else if (Data.corruptedMods?.[id]) {
    type = "C";
    index = Data.corruptedMods[id].tier || "";
    title = _L`Corrupted Mod`;
  }

  return (
    <Tooltip tooltip={<ItemModTip title={title} id={id} group={group} ranges={ranges}/>} align="left">
      <span className="d2p-label">
        {type}{index}
      </span>
    </Tooltip>
  );
}

const itemModSearch = (opt, search) => opt.name.match(search) || opt.mods?.some(id => {
  const name = Data.mods[id].name;
  return (Locale.strings[name] || name)?.match(search);
});

function ItemModLine({ type, item, onChange, group, id, allGroups, canRemove }) {
  const [expanded, setExpanded] = React.useState(false);

  const ranges = React.useMemo(() => {
    if (!group) return null;
    const ranges = [[], [], [], []];
    for (const mid of group) {
      const mod = Data.mods[mid];
      if (!mod) continue;
      if (mod.mod1min != null && mod.mod1max != null) ranges[0].push([mod.mod1min, mod.mod1max]);
      if (mod.mod2min != null && mod.mod2max != null) ranges[1].push([mod.mod2min, mod.mod2max]);
      if (mod.mod3min != null && mod.mod3max != null) ranges[2].push([mod.mod3min, mod.mod3max]);
      if (mod.mod4min != null && mod.mod4max != null) ranges[3].push([mod.mod4min, mod.mod4max]);
    }

    return ranges.map(rng => {
      rng.sort((a, b) => a[0] - b[0]);
      const res = [];
      for (const [a, b] of rng) {
        if (res.length && a <= res[res.length - 1] + 1) {
          res[res.length - 1] = Math.max(res[res.length - 1], b);
        } else {
          res.push(a, b);
        }
      }
      return res;
    });
  }, [group]);

  const lines = React.useMemo(() => {
    if (!ranges) return null;
    const stats = modsToStats({}, {[id]: ranges.map((rng, i) => {
      if (rng.length && rng[0] === rng[rng.length - 1]) {
        return rng[0];
      } else {
        return `{${i}}`;
      }
    })}, {base: item.base, ilvl: item.ilvl});
    if (Object.keys(stats).length > 2) delete stats.corrupted;
    return statsToLines(stats).map(line => line[1].replace(/\xffc\d/g, ''));
  }, [id, item.ilvl, item.base, ranges]);

  const onSetValue = React.useCallback((index, value) => onChange(item => {
    if (!item[type]?.[id]) return item;
    const values = item[type][id].slice();
    values[index] = value;
    item = {...item};
    item[type] = {...item[type]};

    const newId = findLowestMod(group, values);
    if (newId && newId !== id) {
      const mod = Data.mods[newId];
      for (let i = 0; i < values.length; ++i) {
        if (mod[`mod${i + 1}min`] != null && values[i] < mod[`mod${i + 1}min`]) {
          values[i] = mod[`mod${i + 1}min`];
        }
        if (mod[`mod${i + 1}max`] != null && values[i] > mod[`mod${i + 1}max`]) {
          values[i] = mod[`mod${i + 1}max`];
        }
      }
      delete item[type][id];
      item[type][newId] = values;
      if (mod.level) item.ilvl = Math.max(item.ilvl, mod.level);
    } else {
      item[type][id] = values;
    }
    return item;
  }), [type, id, onChange, group]);

  const value = React.useMemo(() => group ? ({id: group[group.length - 1], name: "name"}) : null, [group]);
  const getOptions = React.useCallback(() => {
    const options = [];
    const makeList = groups => groups.map(g => {
      let reason = null;
      if (!g.mods.includes(id)) {
        //TODO: localization
        if (Object.keys(item[type]).some(m => m !== id && Data.mods[m].group === g.group)) {
          reason = _L`Mod of this type already present on the item`;
        } else if (type === 'staff' && Data.staffMods[g.mods[0]]) {
          const tier = Data.staffMods[g.mods[0]].tier;
          if (Object.keys(item.staff).some(m => m !== id && Data.staffMods[m] && Math.abs(Data.staffMods[m].tier - tier) > 18)) {
            reason = _L`Staff mods cannot be more than 3 tiers apart`;
          }
        } else if (g.type === "prefix" || g.type === "suffix") {
          const count = itemCountMods(item, id);
          const limit = (item.quality === Quality.MAGIC ? 1 : 3);
          if (count[g.type] >= limit) {
            reason = (g.type === "prefix" ? _L`No space for more prefixes` : _L`No space for more suffixes`);
          }
        }
        if (!reason && Data.mods[g.mods[0]]?.sockets) {
          if (item.unique) {
            const unique = Data.uniqueItems[item.unique] || Data.setItems[item.unique];
            if (unique && unique.sockets) {
              reason = _L`Cannot have multiple socket mods`;
            }
          }
          for (const mods of [item.corrupted, item.crafted, item.superior, item.mods, item.auto, item.staff]) {
            if (mods && Object.keys(mods).some(m => Data.mods[m]?.sockets)) {
              reason = _L`Cannot have multiple socket mods`;
              break;
            }
          }
        }
      }
      return {
        id: g.mods[g.mods.length - 1],
        mods: g.mods,
        name: g.name,
        disabled: !!reason,
        reason,
      };
    });
    //TODO: localization
    if (allGroups.corrupted) options.push(...makeList(allGroups.corrupted)); 
    if (allGroups.crafted) options.push(...makeList(allGroups.crafted)); 
    if (allGroups.superior) options.push(...makeList(allGroups.superior)); 
    if (allGroups.prefix) options.push({title: "Prefixes", options: makeList(allGroups.prefix)});
    if (allGroups.suffix) options.push({title: "Suffixes", options: makeList(allGroups.suffix)});
    if (allGroups.auto) options.push(...makeList(allGroups.auto));
    if (allGroups.staff) options.push(...makeList(allGroups.staff));
    return options;
  }, [type, item, id, allGroups]);

  const isExpandable = React.useMemo(() => {
    const options = getOptions();
    const count = options.reduce((prev, cur) => prev + (cur.options ? cur.options.length : 1), 0);
    return count > 0 && (!id || count > 1);
  }, [getOptions, id]);

  const onClick = React.useCallback(e => {
    if (!expanded && isExpandable && !e.target.closest(".d2p-HoverNumber")) {
      setExpanded(true);
    }
  }, [expanded, isExpandable]);

  const used = [];
  const contents = id ? (
    <div className={classNames("d2p-mod-contents", {"d2p-expandable": isExpandable})} onClick={onClick}>
      {lines.map((line, i) => (
        <div key={i}>
          {replaceReact(line, /{(\d)(?:;(\w+))?}/g, (m, index, func) => {
            index = parseInt(index);
            switch (func) {
            case 'poison':
              if (used[index]) {
                return Math.round(item[type][id][index] * (item.stats.poisonlength || 0) / 256);
              } else {
                const factor = (item.stats.poisonlength || 0) / 256;
                const rng = ranges[index].map(x => Math.round(x * factor));
                return <HoverNumber digits={0} value={Math.round(item[type][id][index] * factor)} onChange={value => onSetValue(index, Math.round(value / factor))}
                  values={rng} min={rng[0]} max={rng[rng.length - 1]} disabled={expanded}/>;
              }
            case 'per128':
              if (used[index]) {
                return (item[type][id][index] * 100 / 128) | 0;
              } else {
                const rng = ranges[index].map(x => (x * 100 / 128) | 0);
                return <HoverNumber digits={0} value={(item[type][id][index] * 100 / 128) | 0} onChange={value => onSetValue(index, Math.ceil(value * 128 / 100))}
                  values={rng} min={rng[0]} max={rng[rng.length - 1]} disabled={expanded}/>;
              }
            default:
              if (used[index]) {
                return item[type][id][index];
              } else {
                const rng = ranges[index];
                return <HoverNumber digits={0} value={item[type][id][index]} onChange={value => onSetValue(index, value)} values={rng} min={rng[0]} max={rng[rng.length - 1]} disabled={expanded}/>;
              }
            }
          })}
        </div>
      ))}
      {!expanded && (
        <div className="d2p-mods-info">
          <ItemModInfo id={id} group={group} ranges={ranges}/>
          {!!isExpandable && <FontAwesomeIcon icon={faChevronDown} className="d2p-arrow"/>}
        </div>
      )}
    </div>
  ) : (
    <div className="d2p-mod-contents d2p-expandable" onClick={onClick}>
      <div>Add Mod</div>
    </div>
  );

  const renderOption = React.useCallback((opt, search) => {
    const option = <span>{search ? underlinerFunc(opt.name, search) : opt.name}</span>
    if (opt.disabled) {
      return (
        <Tooltip tooltip={opt.reason} align="left">
          {option}
        </Tooltip>
      );
    } else {
      return option;
    }
  }, []);

  const onSelect = React.useCallback(({id: newId}) => onChange(item => {
    const mod = Data.mods[newId];
    if (!mod || (group && group.includes(newId))) return item;
    item = {...item};
    item[type] = {...item[type]};
    if (id) delete item[type][id];
    const values = [];
    if (mod.mod1max != null) values[0] = mod.mod1max;
    if (mod.mod2max != null) values[1] = mod.mod2max;
    if (mod.mod3max != null) values[2] = mod.mod3max;
    if (mod.mod4max != null) values[3] = mod.mod4max;
    item[type][newId] = values;
    if (mod.level) item.ilvl = Math.max(item.ilvl, mod.level);
    return item;
  }), [type, onChange, id, group]);

  const selectRef = React.useRef();
  React.useEffect(() => {
    if (expanded && selectRef.current) {
      selectRef.current.focus();
    }
  }, [expanded]);

  const onClose = React.useCallback(() => setExpanded(false), []);

  const onRemove = React.useCallback(() => onChange(item => {
    item = {...item};
    item[type] = {...item[type]};
    delete item[type][id];
    return item;
  }), [type, id, onChange]);

  //TODO: localization
  return (
    <li className={id ? null : "d2p-empty"}>
      {!!(canRemove && id) && (
        <span className="d2p-mod-remove" title={`Remove Stat`} onClick={onRemove}>
          <FontAwesomeIcon icon={faTimes}/>
        </span>
      )}
      {expanded ? (
        <Select options={getOptions}
                renderValue={() => contents}
                placeholder={contents}
                renderOption={renderOption}
                value={value}
                onChange={onSelect}
                searchLimit={10}
                searchFunc={itemModSearch}
                searchMiddle={true}
                className="d2p-ModSelect"
                ref={selectRef}
                onClose={onClose}
                />
      ) : contents}
    </li>
  );
}

function BasicItemSection({ item, onChange, type, modGroups, children, canRemove }) {
  if (!modGroups[type]) return null;

  const id = Object.keys(item[type])[0];
  const group = id && findModGroup(modGroups, id);

  return (
    <div className="d2p-ItemMods">
      {children}
      <ul className="d2p-ItemModList">
        <ItemModLine type={type} item={item} onChange={onChange} group={group?.mods} id={id} allGroups={modGroups} canRemove={canRemove}/>
      </ul>
    </div>
  );
}

function ItemCorruptedSection(props) {
  if (!props.modGroups.corrupted?.length) return null;
  return (
    <BasicItemSection type="corrupted" {...props} canRemove={true}>
      <h3 className={`d2-color-${Color.RED}`}>{_L`Corrupted Mod`}</h3>
    </BasicItemSection>
  );
}

const ItemCraftedSection = props => (
  <BasicItemSection type="crafted" {...props}>
    <h3 className={`d2-color-${Color.ORANGE}`}>{_L`Crafted Mod`}</h3>
  </BasicItemSection>
);

const ItemAutoSection = props => (
  <BasicItemSection type="auto" {...props}>
    <h3 className={`d2-color-${Color.WHITE}`}>{_L`Auto Mod`}</h3>
  </BasicItemSection>
);

export const ItemSuperiorSection = props => (
  <BasicItemSection type="superior" {...props} canRemove={props.item.quality === Quality.RUNEWORD}>
    <h3 className={`d2-color-${Color.WHITE}`}>{_L`Superior Mod`}</h3>
  </BasicItemSection>
);

function GenericUniqueSection({ item, onChange, children }) {
  const unique = (Data.uniqueItems[item.unique] || Data.setItems[item.unique] || Data.runes[item.unique]);
  const isRuneword = (item.quality === Quality.RUNEWORD);

  const ranges = React.useMemo(() => {
    if (!unique) return null;
    const ranges = [];
    if (isRuneword) {
      for (let i = 1; i <= 7; ++i) {
        if (unique[`t1code${i}`]) {
          if (unique[`t1min${i}`] >= unique[`t1max${i}`]) {
            ranges[i - 1] = unique[`t1max${i}`];
          } else {
            ranges[i - 1] = [unique[`t1min${i}`], unique[`t1max${i}`]];
          }
        }
      }
    } else {
      for (let i = 1; i <= 12; ++i) {
        if (unique[`prop${i}`]) {
          if (unique[`min${i}`] >= unique[`max${i}`]) {
            ranges[i - 1] = unique[`max${i}`];
          } else {
            ranges[i - 1] = [unique[`min${i}`], unique[`max${i}`]];
          }
        }
      }
    }
    return ranges;
  }, [unique, isRuneword]);

  const lines = React.useMemo(() => {
    if (!ranges) return null;
    const values = ranges.map((rng, i) => Array.isArray(rng) ? `{${i}}` : rng);
    const stats = (isRuneword ? runewordStats : uniqueStats)({}, unique, values, {base: item.base, ilvl: item.ilvl});
    return statsToLines(stats).map(line => line[1]);
  }, [ranges, unique, item.ilvl, item.base, isRuneword]);

  const onSetValue = React.useCallback((index, value) => onChange(item => {
    if (ranges[index] && Array.isArray(ranges[index])) {
      value = Math.max(value, ranges[index][0]);
      value = Math.min(value, ranges[index][1]);
    }
    const values = item.uniqueValues.slice();
    values[index] = value;
    return {...item, uniqueValues: values};
  }), [ranges, onChange]);

  const used = [];
  return (
    <div className="d2p-ItemMods">
      {children}
      <ul className="d2p-ItemModList">
        <li>
          <div className="d2p-mod-contents">
            {lines.map((line, i) => (
              <div key={i}>
                {replaceReact(line, /{(\d)(?:;(\w+))?}/g, (m, index, func) => {
                  index = parseInt(index);
                  const rng = ranges[index];
                  switch (func) {
                  case 'skill':
                    return (
                      <select value={item.uniqueValues[index]} onChange={e => onSetValue(index, parseInt(e.target.value))}>
                        {[...Array(rng[1] - rng[0] + 1)].map((_, x) => x + rng[0]).map(value => {
                          const skill = Data.skills[value];
                          const skilldesc = skill && Data.skillDesc[skill.skilldesc];
                          if (!skilldesc) return null;
                          return [Locale.strings[skilldesc.strname], <option key={value} value={value}>{Locale.strings[skilldesc.strname]}</option>];
                        }).sort((a, b) => a[0].localeCompare(b[0])).map(a => a[1])}
                      </select>
                    );
                  case 'skillclass':
                    const skill = Data.skills[item.uniqueValues[index]];
                    if (!skill.charclass) return '';
                    return Locale.istrings[Data.strClassOnly[Data.classes.indexOf(skill.charclass)]];
                  case 'classskills':
                    return (
                      <select value={item.uniqueValues[index]} onChange={e => onSetValue(index, parseInt(e.target.value))}>
                        {[...Array(rng[1] - rng[0] + 1)].map((_, x) => x + rng[0]).map(value => (
                          <option key={value} value={value}>{Data.info.legacyFormat ? Locale.istrings[Data.strClassSkillsStat[value]] : formatStr(Locale.istrings[Data.strClassSkillsStat[value]], 3)}</option>
                        ))}
                      </select>
                    );
                  case 'lvl':
                    return (
                      <React.Fragment>
                        <HoverNumber digits={2} value={item.uniqueValues[index] / 8} onChange={value => onSetValue(index, Math.round(value * 8))} min={rng[0] / 8} max={rng[1] / 8} step={0.125}/>/lvl
                      </React.Fragment>
                    );
                  default:
                    if (used[index]) {
                      return item.uniqueValues[index];
                    } else {
                      return <HoverNumber digits={0} value={item.uniqueValues[index]} onChange={value => onSetValue(index, value)} min={rng[0]} max={rng[1]}/>;
                    }
                  }
                })}
              </div>
            ))}
          </div>
        </li>
      </ul>
    </div>
  );
}

const ItemUniqueSection = props => (
  <GenericUniqueSection {...props}>
    <h3 className={`d2-color-${Color.GOLD}`}>{_L`Unique`}</h3>
  </GenericUniqueSection>
);

const ItemSetSection = props => (
  <GenericUniqueSection {...props}>
    <h3 className={`d2-color-${Color.GREEN}`}>{_L`Set`}</h3>
  </GenericUniqueSection>
);

const ItemRunewordSection = props => (
  <GenericUniqueSection {...props}>
    <h3 className={`d2-color-${Color.GOLD}`}>{_L`Runeword`}</h3>
  </GenericUniqueSection>
);

function ItemBluntSection({ item }) {
  const types = itemGetTypes(item.base);
  if (!types.has('blun')) return null;
  const stats = {item_undeaddamage_percent: 50};
  const lines = statsToLines(stats).map(line => line[1]);
  return (
    <div className="d2p-ItemMods">
      <h3 className={`d2-color-${Color.WHITE}`}>{_L`Blunt Weapon`}</h3>
      <ul className="d2p-ItemModList">
        <li>
          <div className="d2p-mod-contents">
            {lines.map((line, i) => <div key={i}>{line}</div>)}
          </div>
        </li>
      </ul>
    </div>
  );
}

export function ItemModsSection({ item, onChange, modGroups }) {
  const canAdd = React.useMemo(() => {
    const count = itemCountMods(item);
    switch (item.quality) {
    case Quality.MAGIC: return count.prefix + count.suffix < 2;
    case Quality.RARE: return count.prefix + count.suffix < (isSubType(Data.items[item.base]?.type, 'jewl') ? 4 : 6);
    case Quality.CRAFTED: return count.prefix + count.suffix < 4;
    default: return false;
    }
  }, [item]);

  const lines = Object.keys(item.mods).map(id => ({group: findModGroup(modGroups, id), id}));
  lines.sort((a, b) => b.group.prio - a.group.prio);

  return (
    <div className="d2p-ItemMods">
      <h3 className={`d2-color-${Color.BLUE}`}>{_L`Regular Mods`}</h3>
      <ul className="d2p-ItemModList">
        {lines.map(({group, id}) => (
          <ItemModLine type="mods" item={item} onChange={onChange} group={group.mods} allGroups={modGroups} id={id} key={group.mods[0]} canRemove/>
        ))}
        {canAdd && <ItemModLine type="mods" item={item} onChange={onChange} allGroups={modGroups}/>}
      </ul>
    </div>
  );
}

function ItemStaffSection({ item, onChange, modGroups }) {
  if (!modGroups.staff) return null;

  const canAdd = Object.keys(item.staff).length < 3;

  const lines = Object.keys(item.staff).map(id => ({group: findModGroup(modGroups, id), id}));
  lines.sort((a, b) => b.group.prio - a.group.prio);

  return (
    <div className="d2p-ItemMods">
      <h3 className={`d2-color-${Color.WHITE}`}>{_L`Staff Mods`}</h3>
      <ul className="d2p-ItemModList">
        {lines.map(({group, id}) => (
          <ItemModLine type="staff" item={item} onChange={onChange} group={group.mods} allGroups={modGroups} id={id} key={group.mods[0]} canRemove/>
        ))}
        {canAdd && <ItemModLine type="staff" item={item} onChange={onChange} allGroups={modGroups}/>}
      </ul>
    </div>
  );
}

const renderSocket = ({ id, name, color, description, create }, search) => (create ? (
  <React.Fragment>
    <D2ItemTooltip base={create}>
      <span className={`d2-color-${color}`}>{search ? underlinerFunc(name, search) : name}</span>
    </D2ItemTooltip>
    {!!description && <span className="d2p-item-description">{description}</span>}
  </React.Fragment>
) : (
  <React.Fragment>
    <D2ItemTooltip id={id}>
      <span className={`d2-color-${color}`}>{search ? underlinerFunc(name, search) : name}</span>
    </D2ItemTooltip>
    {!!description && <span className="d2p-item-description">{description}</span>}
  </React.Fragment>
));

function ItemSocketLine({ index, item, onChange }) {
  const id = item.socketedItems?.[index];
  const sock = Store.useItem(id) || presetItems[id];
  const store = Store.useStore();
  const dispatch = Store.useDispatch();

  const value = sock && {id, name: itemGetName(sock), color: itemGetColor(sock), description: sock.description};

  const getOptions = React.useCallback(() => {
    const items = {...store.getState().items, ...presetItems};
    const runes = [], gems = [], jewels = [];
    for (const id in items) {
      const item = items[id];
      const types = itemGetTypes(item.base);
      if (types.has('sock')) {
        const opt = {id, name: itemGetName(item), color: itemGetColor(item)};
        if (types.has('rune')) {
          runes.push(opt);
        } else if (types.has('gem')) {
          gems.push(opt);
        } else {
          opt.description = item.description;
          jewels.push(opt);
        }
      }
    }
    jewels.sort((a, b) => a.name.localeCompare(b.name));
    runes.reverse();
    gems.reverse();
    return [
      {title: _L`Jewels`, options: jewels},
      {title: _L`Runes`, options: runes},
      {title: _L`Gems`, options: gems},
      {title: _L`Create Jewel`, options: [
        {id: 'new/jew', name: Locale.strings.jew, color: Color.BLUE, create: 'jew'},
        ...Object.entries(Data.uniqueItems).map(([id, uniq]) => {
          if (uniq.code !== 'jew') return null;
          return {id: `new/${id}`, name: Locale.strings[uniq.index] || uniq.index, color: Color.GOLD, create: id, description: uniq.suffix};
        }).filter(Boolean),
      ]},
    ].filter(opt => opt.options.length > 0);
  }, [store]);

  const editDispatch = EditingContext.useDispatch();
  const onSetValue = React.useCallback(value => {
    let id = value?.id;
    if (value?.create) {
      const item = (Data.items[value.create] ? itemCreateNormal(value.create) : itemCreateUnique(value.create));
      if (!item) return;
      const items = store.getState().items;
      id = 1;
      while (items[id]) id += 1;
      dispatch.setItem(id, item);
      editDispatch({type: "item", id});
    }
    onChange(item => {
      item = {...item};
      const socks = item.socketedItems?.slice() || [];
      socks[index] = id || null;
      item.socketedItems = socks.filter(Boolean);
      return item;
    });
  }, [index, onChange, store, dispatch, editDispatch]);

  const renderValue = React.useCallback(({ id, name, color, description }, search) => {
    const onClick = e => {
      if (presetItems[id]) return;
      editDispatch({type: "item", id});
      e.stopPropagation();
      return false;
    };
    return (
      <React.Fragment>
        <D2ItemTooltip id={id}>
          <span className={`d2-color-${color}`}>{search ? underlinerFunc(name, search) : name}</span>
        </D2ItemTooltip>
        {!presetItems[id] && <span className="d2p-item-navigate" onClick={onClick}>{_L`(edit)`}</span>}
      </React.Fragment>
    );
  }, [editDispatch]);

  return (
    <Select options={getOptions}
      renderValue={renderValue}
      placeholder={_L`Empty Socket`}
      renderOption={renderSocket}
      value={value}
      onChange={onSetValue}
      searchLimit={10}
      searchMiddle={true}
      className="d2p-SocketSelect ui-Select-noframe"
      disabled={item.quality === Quality.RUNEWORD}
      allowEmpty
    />
  );
}

function ItemCustomSection({ item }) {
  const lines = React.useMemo(() => statsToLines({...item.stats}).map(line => line[1]), [item.stats]);

  return (
    <div className="d2p-ItemMods">
      <h3 className={`d2-color-${Color.WHITE}`}>{_L`Imported Item`}</h3>
      {lines.length > 0 && (
        <ul className="d2p-ItemModList">
          <li>
            <div className="d2p-mod-contents">
              {lines.map((line, i) => <div key={i}>{line}</div>)}
            </div>
          </li>
        </ul>
      )}
    </div>
  );
}

function ItemSocketsSection({ item, onChange }) {
  const socketedItems = Store.useSelector(state => item.socketedItems?.map(id => state.items[id] || presetItems[id]), [item.socketedItems]);
  const lines = React.useMemo(() => {
    const stats = {};
    const types = itemGetTypes(item.base);
    if (socketedItems) {
      for (const sock of socketedItems) {
        if (!sock) continue;
        gemStats(stats, sock.base, types);
        if (sock.stats) {
          for (const id in sock.stats) {
            stats[id] = (stats[id] || 0) + sock.stats[id];
          }
        }
      }
    }
    return statsToLines(stats).map(line => line[1]);
  }, [item.base, socketedItems]);

  const runeword = React.useMemo(() => {
    if (item.quality !== Quality.NORMAL && item.quality !== Quality.SUPERIOR && item.quality !== Quality.LOW) return null;
    if (item.sockets !== item.socketedItems.length) return null;
    if (item.socketedItems.some(id => Data.misc[id]?.type !== 'rune')) return null;
    const types = itemGetTypes(item.base);
    return Object.keys(Data.runes).find(id => {
      const uniq = Data.runes[id];
      const runes = [uniq.rune1, uniq.rune2, uniq.rune3, uniq.rune4, uniq.rune5, uniq.rune6].filter(Boolean);
      if (runes.length !== item.socketedItems.length) return false;
      if (runes.some((id, idx) => id !== item.socketedItems[idx])) return false;
      if (![uniq.itype1, uniq.itype2, uniq.itype3, uniq.itype4, uniq.itype5, uniq.itype6].some(t => t && types.has(t))) return false;
      if ([uniq.etype1, uniq.etype2, uniq.etype3].some(t => t && types.has(t))) return false;
      return true;
    });
  }, [item.base, item.sockets, item.socketedItems, item.quality]);

  const onCreateRw = React.useCallback(() => onChange(item => itemCreateRuneword(runeword, item.base, item)), [onChange, runeword]);

  return (
    <div className="d2p-ItemMods">
      <h3 className={`d2-color-${Color.GRAY}`}>
        {_L`Sockets`}{!!runeword && ' '}
        {!!runeword && (
          <span className="d2p-create-runeword" onClick={onCreateRw}>
            {_L`(Create ${<span className={`d2-color-${Color.GOLD}`}>{Locale.strings[Data.runes[runeword].name]}</span>})`}
          </span>
        )}</h3>
      <ul className="d2p-ItemModList">
        <li>
          <div className="d2p-mod-contents d2p-socket-contents">
            {[...Array(item.sockets)].map((_, i) => <ItemSocketLine item={item} onChange={onChange} index={i} key={i}/>)}
          </div>
        </li>
        {lines.length > 0 && (
          <li>
            <div className="d2p-mod-contents">
              {lines.map((line, i) => <div key={i}>{line}</div>)}
            </div>
          </li>
        )}
      </ul>
    </div>
  );
}

function useItemBases(item) {
  const gameOptions = Data.info.options;
  const itemQuality = item.quality;
  const itemBase = item.base;
  const itemUnique = item.unique;
  const itemSockets = item.sockets;
  const craftedMod = item.crafted && Object.keys(item.crafted)[0];
  return React.useMemo(() => {
    switch (itemQuality) {
    case Quality.SET:
      if (gameOptions.setUpgrade) {
        const uniq = Data.setItems[itemUnique];
        const uniqBase = uniq && Data.items[uniq.item];
        if (!uniqBase || !uniqBase.normcode) return [itemBase];
        const bases = [uniqBase.normcode, uniqBase.ubercode, uniqBase.ultracode].filter(id => Data.items[id]);
        const index = bases.indexOf(uniq.item);
        if (index > 0) {
          bases.splice(0, index);
        }
        return bases;
      } else {
        return [itemBase];
      }
    case Quality.UNIQUE:
      {
        const uniq = Data.uniqueItems[itemUnique];
        const uniqBase = uniq && Data.items[uniq.code];
        if (!uniqBase || !uniqBase.normcode) return [itemBase];
        const bases = [uniqBase.normcode, uniqBase.ubercode, uniqBase.ultracode].filter(id => Data.items[id]);
        const index = bases.indexOf(uniq.code);
        if (index > 0) {
          bases.splice(0, index);
        }
        return bases;
      }
    case Quality.CRAFTED:
      {
        const p = Data.crafted[craftedMod]?.input1.split(',');
        if (p && p[2] === 'upg') {
          const craftBase = Data.items[p[0]];
          if (!craftBase) return [itemBase];
          return [craftBase.normcode, craftBase.ubercode, craftBase.ultracode].filter(id => Data.items[id]);
        }
      }
      // fallthrough
    default:
      {
        const orig = Data.items[itemBase];
        return Object.keys(Data.items).filter(id => {
          const base = Data.items[id];
          if (itemQuality === Quality.RUNEWORD && itemMaxSockets(base, itemQuality) < itemSockets) return false;
          return base.spawnable && base.type === orig.type && base['2handed'] === orig['2handed'] && base['1or2handed'] === orig['1or2handed'];
        }).sort((a, b) => Data.items[a].level < Data.items[b].level);
      }
    }
  }, [itemQuality, itemBase, itemUnique, craftedMod, itemSockets, gameOptions.setUpgrade]);
}

function itemCanEthereal(item, base) {
  if (item.quality === Quality.SET) return false;
  if (item.quality !== Quality.RUNEWORD && item.stats?.item_indesctructible) return false;
  if (item.quality === Quality.CRAFTED && !Data.info.pd2) return false;
  if (baseNoDurability(base, item.quality)) return false;
  return true;
}

function ItemOptions({ isPreset, item, onChange }) {
  const minIlvl = React.useMemo(() => itemMinLevel(item), [item]);
  const setIlvl = React.useCallback(value => onChange(item => itemUpdate({...item, ilvl: value})), [onChange]);

  const setQuality = React.useCallback(e => onChange(item => itemSetQuality(item, parseInt(e.target.value))), [onChange]);

  const base = Data.items[item.base];
  const type = base && Data.itemTypes[base.type];
  //const canCraft = React.useMemo(() => base && Object.values(Data.crafted).some(mod =>
  //  itemCheckMod(mod, base, itemGetTypes(item.base), Quality.MAGIC, 100)), [base, item.base]);

  const onRename = React.useCallback(e => onChange(item => ({...item, name: e.target.value.substring(0, 32)})), [onChange]);
  const onRandomize = React.useCallback(() => onChange(item => ({...item, name: randomRareName(itemGetTypes(item.base))})), [onChange]);

  const defRange = itemDefenseRange(item);
  const setDefense = React.useCallback(value => onChange(item => itemUpdate({...item, defense: value})), [onChange]);

  const gameOptions = Data.info.options;

  const canEthereal = itemCanEthereal(item, base);
  const canDoubleEth = (item.quality === Quality.NORMAL || (item.quality === Quality.RUNEWORD && !Object.keys(item.superior || {}).length)) &&
    base.maxac && item.sockets && gameOptions.etherealBug;
  const setEthereal = React.useCallback(e => canEthereal && onChange(item => itemUpdate({...item, ethereal: !!e.target.checked})), [canEthereal, onChange]);
  const setEthBugged = React.useCallback(e => canEthereal && onChange(item => itemUpdate({...item, ethbugged: !!e.target.checked})), [canEthereal, onChange]);

  const maxSockets = itemMaxSockets(base, item.quality);
  const setSockets = React.useCallback(value => onChange(item => {
    item = {...item, sockets: value};
    if (item.socketedItems?.length > value) {
      item.socketedItems = item.socketedItems.slice(0, value);
    }
    return itemUpdate(item);
  }), [onChange]);

  const bases = useItemBases(item).filter(id => Data.items[id].invwidth === base.invwidth && Data.items[id].invheight === base.invheight);
  const getBaseOptions = React.useCallback(() => bases.map(id => {
    const item = Data.items[id];
    return {id, name: Locale.strings[item.namestr] || item.name};
  }), [bases]);
  const renderBaseOption = React.useCallback(({ id, name }, search) => (
    <D2ItemTooltip base={id}>
      <span>{search ? underlinerFunc(name, search) : name}</span>
    </D2ItemTooltip>
  ), []);
  const onSelectBase = React.useCallback(value => onChange(item => itemSetBase(item, value.id)), [onChange]);

  const setIconIndex = React.useCallback(value => onChange(item => ({...item, iconIndex: value - 1})), [onChange]);

  if (!type) return null;

  return (
    <ul className="d2p-ItemOptions">
      <li>
        {_L`Item Level:`}{' '}
        <HoverNumber value={item.ilvl} onChange={setIlvl} min={1} max={99} digits={0} disabled={isPreset}/>
        {item.ilvl < minIlvl && <span> ({_L`item mods require ${minIlvl}`})</span>}
      </li>
      {!item.custom && item.quality !== Quality.UNIQUE && item.quality !== Quality.SET && item.quality !== Quality.RUNEWORD && item.quality !== Quality.CRAFTED && (!type.magic || type.rare) && (
        <li>
          {_L`Quality:`}{' '}
          <select value={item.quality} onChange={setQuality} disabled={isPreset}>
            {!type.magic && <option value={Quality.LOW}>{_L`Low`}</option>}
            {!type.magic && <option value={Quality.NORMAL}>{_L`Normal`}</option>}
            {!type.magic && <option value={Quality.SUPERIOR}>{_L`Superior`}</option>}
            {!type.normal && <option value={Quality.MAGIC}>{_L`Magic`}</option>}
            {!!type.rare && <option value={Quality.RARE}>{_L`Rare`}</option>}
            {/*!!canCraft && <option value={Quality.CRAFTED}>{_L`Crafted`}</option>*/}
          </select>
        </li>
      )}
      {!!type?.varinvgfx && (
        <li>
          {_L`Icon variant:`}{' '}
          <HoverNumber value={(item.iconIndex || 0) + 1} onChange={setIconIndex} min={1} max={type.varinvgfx} digits={0} disabled={isPreset}/>
        </li>
      )}
      {(item.quality === Quality.RARE || item.quality === Quality.CRAFTED) && (
        <li>
          {_L`Name:`}{' '}
          <input type="text" value={item.name || ''} onChange={onRename} placeholder={_L`Rare Name`} disabled={isPreset}/>
          <button onClick={onRandomize} disabled={isPreset}>{_L`Random`}</button>
        </li>
      )}
      {!item.custom && bases.length > 1 && (
        <li>
          {_L`Base:`}{' '}
          <Select options={getBaseOptions}
                  renderValue={renderBaseOption}
                  renderOption={renderBaseOption}
                  value={{id: item.base, name: Locale.strings[base.namestr] || base.name}}
                  onChange={onSelectBase}
                  searchLimit={10}
                  searchMiddle={true}
                  disabled={isPreset}
                  />
        </li>
      )}
      {!!(defRange && defRange.minac < defRange.maxac) && (
        <li>
          {_L`Defense:`}{' '}
          <HoverNumber value={item.defense || defRange.maxac} onChange={setDefense} min={defRange.minac} max={defRange.maxac} digits={0} disabled={isPreset}/>
        </li>
      )}
      {!!(maxSockets > 0 && item.quality !== Quality.RUNEWORD && !item.stats.item_numsockets) && (
        <li>
          {_L`Sockets:`}{' '}
          <HoverNumber value={item.sockets || 0} onChange={setSockets} min={0} max={maxSockets} digits={0} disabled={isPreset}/>
        </li>
      )}
      {item.stats?.item_ethereal ? (
        <li>
          <label><input type="checkbox" checked={true} readOnly/> {_L`Ethereal`}</label>
        </li>
      ) : (!!canEthereal && (
        <li>
          <div><label><input type="checkbox" checked={!!item.ethereal} onChange={setEthereal} readOnly={isPreset}/> {_L`Ethereal`}</label></div>
          {!!(canDoubleEth && item.ethereal) && (
            <div><label><input type="checkbox" checked={!!item.ethbugged} onChange={setEthBugged} readOnly={isPreset}/> {_L`Ethereal Bugged`}</label></div>
          )}
        </li>
      ))}
    </ul>
  );
}

function formatOdds(value) {
  if (value <= 0) return '0%';
  if (value >= 1) return '100%';
  const digits = Math.max(0, 4 - (1 / value).toFixed(0).length);
  const ratio = <span className="d2p-value">1/{formatNumber(1 / value, digits, 10000, true)}</span>
  if (value > 0.0001) {
    return <>{ratio} ({parseFloat((value * 100).toFixed(2))}%)</>;
  } else {
    return ratio;
  }
}

function itemDropLink(item) {
  const info = {};
  switch (item.quality) {
  case Quality.UNIQUE:
    info.type = 'unique';
    info.id = item.unique;
    break;
  case Quality.SET:
    info.type = 'set';
    info.id = item.unique;
    break;
  case Quality.RUNEWORD:
    info.type = 'base';
    info.id = item.base;
    info.quality = (item.superior && Object.keys(item.superior).length ? Quality.SUPERIOR : Quality.NORMAL);
    break;
  case Quality.CRAFTED:
    info.type = 'base';
    info.id = item.base;
    info.quality = Quality.MAGIC;
    break;
  default:
    info.type = 'base';
    info.id = item.base;
    info.quality = item.quality;
  }
  const url = new URL('https://maxroll.gg/d2/d2-drop-calculator');
  const state = {...defaultState(), mode: 'item', item: info};
  if (item.quality === Quality.UNIQUE && Data.info?.patch) {
    const unique = Data.uniqueItems[item.unique];
    if (unique && unique.ladder) {
      state.settings.terrorized = true;
    }
  }
  url.hash = '#' + serializeState(state);
  return url.href;
}

function TraderieLink({ item }) {
  if (!item.unique) return null;
  const data = Data.uniqueItems[item.unique] || Data.setItems[item.unique] || Data.runes[item.unique];
  if (!data || !data.traderie) return null;
  return (
    <div className="d2p-traderie-info">
      Find this item on <a href={`https://traderie.com/diablo2resurrected/product/${data.traderie}`} target="_blank" rel="noopener noreferrer">
        <span className="d2p-traderie-logo"/> Traderie
      </a>
    </div>
  );
}

export default React.memo(function ItemEditor({ id }) {
  const item = Store.useItem(id) || presetItems[id];
  const dispatch = Store.useDispatch();
  const onChange = React.useCallback(value => !presetItems[id] && dispatch.setItem(id, value), [id, dispatch]);
  const onChangeEx = React.useCallback(value => !presetItems[id] && dispatch.setItem(id, (item, state) => {
    const newItem = value(item);
    if (newItem === item) return item;
    return itemUpdate(newItem, state);
  }), [id, dispatch]);
  const gfx = React.useContext(GameContext).gfx;

  const itemBase = item?.base;
  const itemQuality = item?.quality;
  const itemLevel = item?.ilvl;
  const modGroups = React.useMemo(() => itemBase && itemGetModGroups(itemBase, itemQuality, itemLevel), [itemBase, itemQuality, itemLevel]);

  const itemMover = useItemMover();
  const [, drag] = useDragEx({
    type: "ITEM",
    item: () => ({type: "ITEM", id}),
    end: (item, monitor) => itemMover({location: "library", slot: id}, monitor.getDropResult()),
  }, [id, itemMover]);

  const probability = React.useMemo(() => item && itemProbability(item, modGroups), [item, modGroups]);

  if (!item) return null;

  return <>
    <div className="d2p-ItemEditor">
      <div className="d2p-header">
        <div className={classNames("d2p-item-preview", {"d2p-game-hd": gfx.hd})}>
          <D2ItemTooltip item={item}>
            <ItemImage id={id} sockets={true} ref={drag}/>
          </D2ItemTooltip>
        </div>
        <ItemOptions item={item} onChange={onChange} isPreset={!!presetItems[id]}/>
      </div>
      {item.custom && <ItemCustomSection item={item}/>}
      {!item.custom && (
        <React.Fragment>
          {!!modGroups.corrupted && <ItemCorruptedSection item={item} onChange={onChangeEx} modGroups={{corrupted: modGroups.corrupted}}/>}
          {!!item.crafted && <ItemCraftedSection item={item} onChange={onChangeEx} modGroups={{crafted: modGroups.crafted}}/>}
          {!!item.mods && <ItemModsSection item={item} onChange={onChangeEx} modGroups={{prefix: modGroups.prefix, suffix: modGroups.suffix}}/>}
          {!!(item.quality === Quality.UNIQUE && item.uniqueValues) && <ItemUniqueSection item={item} onChange={onChangeEx}/>}
          {!!(item.quality === Quality.SET && item.uniqueValues) && <ItemSetSection item={item} onChange={onChangeEx}/>}
          {!!(item.quality === Quality.RUNEWORD && item.uniqueValues) && <ItemRunewordSection item={item} onChange={onChangeEx}/>}
          {!!item.auto && <ItemAutoSection item={item} onChange={onChangeEx} modGroups={{auto: modGroups.auto}}/>}
          {!!item.superior && <ItemSuperiorSection item={item} onChange={onChangeEx} modGroups={{superior: modGroups.superior}}/>}
          {!!item.staff && <ItemStaffSection item={item} onChange={onChangeEx} modGroups={{staff: modGroups.staff}}/>}
        </React.Fragment>
      )}
      <ItemBluntSection item={item}/>
      {!!item.sockets && <ItemSocketsSection item={item} onChange={onChangeEx}/>}
      <TraderieLink item={item}/>
      {(probability && (probability.mods < .9999 || probability.values < .9999)) ? (
        <div className="d2p-item-probability">
          {probability.mods < .9999 && <p>{_L`Odds of rolling selected mods: ${formatOdds(probability.mods)}`}</p>}
          {probability.values < .9999 && <p>{_L`Odds of rolling selected values or higher: ${formatOdds(probability.values)}`}</p>}
          <p>{_L`These odds do not factor in getting the desired base or quality to drop.`}</p>
          <p>{_L`Check out our ${
            <a href={itemDropLink(item)} target="_blank" rel="noopener noreferrer">{_L`Drop Calculator`}</a>
          } for those numbers.`}</p>
        </div>
      ) : (
        <div className="d2p-item-probability">
          <p>{_L`Check out this item in our ${
            <a href={itemDropLink(item)} target="_blank" rel="noopener noreferrer">{_L`Drop Calculator`}</a>
          }.`}</p>
        </div>
      )}
    </div>
    <AdUnit id="d2planner-tab-equipment" className="d2p-TabFooter d2planner-tab-ad"/>
  </>;
});
